import React, {useEffect, useRef, useState} from 'react';

// Limit the backing bitmap on mobile while retaining the original document
// coordinate system. Zoom never changes the positions of saved ink strokes.
export function pdfRenderScale(page,zoom=1,pixelRatio=1) {
    const width=Number(page.width),height=Number(page.height);
    if(!(width>0 && height>0))throw new Error('The PDF page dimensions are unavailable.');
    const requested=Math.max(.1,Math.min(8,Number(zoom) || 1))*Math.max(1,Math.min(3,Number(pixelRatio) || 1));
    return Math.min(requested,Math.sqrt(8388608/(width*height)),8192/width,8192/height);
}
function releaseCanvas(canvas) {if(canvas){canvas.width=0;canvas.height=0;canvas.remove();}}

export default function PdfMaterialPage({document:materialDocument,page,zoom=1,onReady,onError,onStatusChange}) {
    const container=useRef(null),previous=useRef(null);
    const callbacks=useRef({onReady,onError,onStatusChange});
    callbacks.current={onReady,onError,onStatusChange};
    const [status,setStatus]=useState('loading');
    const [error,setError]=useState('');
    useEffect(()=>{
        const host=container.current;
        if(!host || !page || !materialDocument)return undefined;
        let active=true,task=null,pdfPage=null,canvas=null;
        const identity={document:materialDocument,key:page.key};
        if(previous.current?.document!==identity.document || previous.current?.key!==identity.key) {
            [...host.querySelectorAll('canvas')].forEach(releaseCanvas);
        }
        previous.current=identity;
        setStatus('loading');setError('');callbacks.current.onStatusChange?.('loading');
        const render=async()=>{
            try {
                pdfPage=await materialDocument.getPdfPage(page.index);
                if(!active){pdfPage.cleanup();return;}
                const scale=pdfRenderScale(page,zoom,window.devicePixelRatio || 1);
                const viewport=pdfPage.getViewport({scale});
                canvas=window.document.createElement('canvas');
                canvas.width=Math.max(1,Math.ceil(viewport.width));canvas.height=Math.max(1,Math.ceil(viewport.height));
                canvas.style.width='100%';canvas.style.height='100%';canvas.style.display='block';
                canvas.setAttribute('role','img');canvas.setAttribute('aria-label',page.label || `PDF page ${page.index+1}`);
                const context=canvas.getContext('2d',{alpha:false});
                if(!context)throw new Error('Your browser could not prepare the PDF page. Please reload the page.');
                task=pdfPage.render({canvas,canvasContext:context,viewport,background:'rgb(255,255,255)'});
                await task.promise;
                if(!active){releaseCanvas(canvas);return;}
                // Swap only the completed frame. A zoom rerender keeps the previous
                // readable page visible, and canceled work can never paint over it.
                [...host.querySelectorAll('canvas')].forEach(releaseCanvas);
                host.appendChild(canvas);
                setStatus('ready');callbacks.current.onStatusChange?.('ready');callbacks.current.onReady?.();
            } catch(reason) {
                if(!active || reason?.name==='RenderingCancelledException' || reason?.name==='AbortError')return;
                [...host.querySelectorAll('canvas')].forEach(releaseCanvas);
                const message=reason?.message || 'This PDF page could not be rendered. Please retry.';
                setError(message);setStatus('error');callbacks.current.onStatusChange?.('error');callbacks.current.onError?.(reason);
            } finally {
                if(canvas && canvas.parentNode!==host)releaseCanvas(canvas);
                pdfPage?.cleanup();
            }
        };
        render();
        return()=>{
            active=false;
            task?.cancel();
            if(task)Promise.resolve(task.promise).catch(()=>{}).finally(()=>{
                if(canvas && canvas.parentNode!==host)releaseCanvas(canvas);
                pdfPage?.cleanup();
            });
        };
    },[materialDocument,page,zoom]);
    useEffect(()=>{
        const host=container.current;
        return()=>{if(host)[...host.querySelectorAll('canvas')].forEach(releaseCanvas);};
    },[]);
    return <div style={{position:'relative',width:'100%',height:page?.height || '100%',background:'#fff'}} aria-busy={status==='loading'}>
        <div ref={container} style={{width:'100%',height:'100%'}} />
        {status==='loading' && <div role="status" style={{position:'absolute',top:12,left:12,padding:'6px 10px',borderRadius:8,background:'rgba(255,255,255,.92)',color:'#6b21a8',fontSize:14}}>Preparing page…</div>}
        {status==='error' && <div role="alert" style={{position:'absolute',inset:0,padding:24,background:'#fff7ed',color:'#9a3412'}}>{error}</div>}
    </div>;
}
